import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";

/*
====================================================================
SOUS-COMPOSANTS UI
====================================================================
*/

/*
Section visuelle de la sidebar

Chaque bloc (Sites, Ouvrages, Levels) est présenté
comme une section indépendante avec :
- un titre
- un contenu
*/
const SidebarSection = ({ title, children, style = {} }) => (
  <div
    style={{
      marginBottom: 18,
      ...style,
    }}
  >
    <div
      style={{
        fontFamily: theme.fonts.heading,
        fontSize: theme.fontSizes.xs,
        fontWeight: theme.fontWeights.semiBold,
        color: theme.colors.text.small,
        marginBottom: 8,
        letterSpacing: "0.2px",
      }}
    >
      {title}
    </div>

    {children}
  </div>
);

/*
Bouton / toggle d’un item de navigation

Utilisé pour :
- Site
- Ouvrage
- Level
*/
const Item = ({ active, viewActive = false, label, onClick }) => (
  <div
    onClick={onClick}
    style={{
      padding: "5px 7px",
      cursor: "pointer",
      borderRadius: theme.borders.radius.sm,
      fontSize: theme.fontSizes.xs,
      background: active
        ? theme.Generic.blue.selected.bg
        : theme.colors.background.surface,
      color: active
        ? theme.Generic.blue.selected.text
        : theme.colors.text.body,
      border: viewActive
        ? `2px solid ${theme.colors.text.secondary}`
        : active
        ? `1px solid ${theme.Generic.blue.selected.bg}`
        : `1px solid ${theme.colors.borders.default}`,
      boxShadow: viewActive ? theme.shadows.xs : "none",
      marginBottom: 6,
      userSelect: "none",
      transition: "all 0.15s ease-in-out",
      boxSizing: "border-box",
    }}
  >
    {label}
  </div>
);


/*
Petit bouton d’action standardisé

Utilisé pour :
- création
- suppression
*/
const ActionButton = ({ children, onClick, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      height: 26,
      padding: "0 8px",
      borderRadius: theme.borders.radius.md,
      border: `1px solid ${theme.colors.borders.default}`,
      background: disabled
        ? theme.colors.background.muted
        : theme.colors.background.input,
      color: disabled
        ? theme.colors.text.verysmall
        : theme.colors.text.body,
      fontFamily: theme.fonts.body,
      fontSize: theme.fontSizes.xs,
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    {children}
  </button>
);

/*
Onglet de navigation du panneau gauche

Deux modes :
- Layout
- Structure
*/
const TabButton = ({ active, label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      height: 28,
      borderRadius: theme.borders.radius.md,
      border: `1px solid ${theme.colors.borders.default}`,
      background: active
        ? theme.Generic.blue.selected.bg
        : theme.colors.background.surface,
      color: active
        ? theme.Generic.blue.selected.text
        : theme.colors.text.body,
      fontFamily: theme.fonts.body,
      fontSize: theme.fontSizes.xs,
      cursor: "pointer",
    }}
  >
    {label}
  </button>
);

/*
====================================================================
COMPOSANT PRINCIPAL
====================================================================
*/

const DrawSiteLevelSidebar = ({
  sites = [],

  activeSiteId,
  activeOuvrageId,
  activeLevelId,

  activeMode,
  setActiveMode,

  setActiveSiteId,
  setActiveOuvrageId,
  setActiveLevelId,

  createSite,
  deleteSite,

  createOuvrage,
  deleteOuvrage,

  createLevel,
  deleteLevel,

  style = {},
}) => {
  /*
  ================================================================
  ONGLET ACTIF DE LA SIDEBAR
  ================================================================
  */

  // "layout" = mode de travail principal
  // "structure" = futur arbre hiérarchique global
  const [activeTab, setActiveTab] = useState("layout");

  /*
  ================================================================
  FILTRAGE HIÉRARCHIQUE
  ================================================================
  */

  // site actif
  const activeSite = useMemo(() => {
    return sites.find((site) => site.id === activeSiteId) ?? null;
  }, [sites, activeSiteId]);

  // ouvrages visibles = uniquement ceux du site actif
  const visibleOuvrages = activeSite?.ouvrages ?? [];

  // ouvrage actif
  const activeOuvrage = useMemo(() => {
    return (
      visibleOuvrages.find((ouvrage) => ouvrage.id === activeOuvrageId) ?? null
    );
  }, [visibleOuvrages, activeOuvrageId]);

  // niveaux visibles = uniquement ceux de l’ouvrage actif
  const visibleLevels = activeOuvrage?.levels ?? [];

  /*
  ================================================================
  HANDLERS DE SÉLECTION
  ================================================================
  */

  /*
  Quand on change de site :
  - on active ce site
  - on active automatiquement le premier ouvrage du site
  - on active automatiquement le premier niveau de cet ouvrage
  */
  const handleSelectSite = (siteId) => {
    const site = sites.find((item) => item.id === siteId) ?? null;
    const firstOuvrage = site?.ouvrages?.[0] ?? null;
    const firstLevel = firstOuvrage?.levels?.[0] ?? null;
    
    setActiveMode("SITE");
    setActiveSiteId(siteId);
    setActiveOuvrageId(firstOuvrage?.id ?? null);
    setActiveLevelId(firstLevel?.id ?? null);
  };

  /*
  Quand on change d’ouvrage :
  - on active cet ouvrage
  - on active automatiquement son premier niveau
  */
  const handleSelectOuvrage = (ouvrageId) => {
    const ouvrage =
      visibleOuvrages.find((item) => item.id === ouvrageId) ?? null;
    const firstLevel = ouvrage?.levels?.[0] ?? null;
    
    setActiveMode("OUVRAGE");
    setActiveOuvrageId(ouvrageId);
    setActiveLevelId(firstLevel?.id ?? null);
  };

  /*
  Quand on change de niveau :
  - on active simplement ce niveau
  */
  const handleSelectLevel = (levelId) => {
    setActiveLevelId(levelId);
    setActiveMode("NIVEAU");
  };


  /*
  ================================================================
  RENDER - ONGLET LAYOUT
  ================================================================
  */

  const renderLayoutTab = () => {
    return (
      <>
        {/* --------------------------------------------------------
            SECTION SITES
        -------------------------------------------------------- */}
        <SidebarSection
          title="Sites"
          style={{
            minHeight: 90,
          }}
        >
          {sites.map((site) => (
            <Item
              key={site.id}
              label={site.label}
              active={site.id === activeSiteId}
              viewActive={site.id === activeSiteId && activeMode === "SITE"}
              onClick={() => handleSelectSite(site.id)}
            />
          ))}

          <div
            style={{
              display: "flex",
              gap: 6,
              marginTop: 8,
            }}
          >
            <ActionButton onClick={createSite}>+ Site</ActionButton>

            <ActionButton
              onClick={() => deleteSite(activeSiteId)}
              disabled={!activeSiteId}
            >
              Delete
            </ActionButton>
          </div>
        </SidebarSection>

        {/* --------------------------------------------------------
            SECTION OUVRAGES
        -------------------------------------------------------- */}
        <SidebarSection
          title="Ouvrages"
          style={{
            minHeight: 150,
          }}
        >
          {visibleOuvrages.map((ouvrage) => (
            <Item
              key={ouvrage.id}
              label={ouvrage.label}
              active={ouvrage.id === activeOuvrageId}
              viewActive={ouvrage.id === activeOuvrageId && activeMode === "OUVRAGE"}
              onClick={() => handleSelectOuvrage(ouvrage.id)}
            />
          ))}

          <div
            style={{
              display: "flex",
              gap: 6,
              marginTop: 8,
            }}
          >
            <ActionButton
              onClick={createOuvrage}
              disabled={!activeSiteId}
            >
              + Ouvrage
            </ActionButton>

            <ActionButton
              onClick={() => deleteOuvrage(activeOuvrageId)}
              disabled={!activeOuvrageId}
            >
              Delete
            </ActionButton>
          </div>
        </SidebarSection>

        {/* --------------------------------------------------------
            SECTION LEVELS
        -------------------------------------------------------- */}
        <SidebarSection
          title="Levels"
          style={{
            minHeight: 220,
            marginBottom: 0,
          }}
        >
          {visibleLevels.map((level) => (
            <Item
              key={level.id}
              label={level.label}
              active={level.id === activeLevelId}
              viewActive={level.id === activeLevelId && activeMode === "NIVEAU"}
              onClick={() => handleSelectLevel(level.id)}
            />
          ))}

          <div
            style={{
              display: "flex",
              gap: 6,
              marginTop: 8,
            }}
          >
            <ActionButton
              onClick={createLevel}
              disabled={!activeOuvrageId}
            >
              + Level
            </ActionButton>

            <ActionButton
              onClick={() => deleteLevel(activeLevelId)}
              disabled={!activeLevelId}
            >
              Delete
            </ActionButton>
          </div>
        </SidebarSection>
      </>
    );
  };

  /*
  ================================================================
  RENDER - ONGLET STRUCTURE
  ================================================================
  */

  /*
  Pour cette étape, on met une version simple et stable :
  - affichage hiérarchique lisible
  - pas encore de repli/dépli
  - pas encore de navigation avancée
  */
  const renderStructureTab = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.xs,
          color: theme.colors.text.body,
          lineHeight: 1.5,
        }}
      >
        {sites.map((site) => (
          <div key={site.id}>
            <div
              style={{
                fontWeight: theme.fontWeights.semiBold,
                color:
                  site.id === activeSiteId
                    ? theme.colors.text.primary
                    : theme.colors.text.body,
              }}
            >
              Site — {site.label}
            </div>

            <div style={{ paddingLeft: 10, marginTop: 4 }}>
              {(site.ouvrages ?? []).map((ouvrage) => (
                <div key={ouvrage.id} style={{ marginBottom: 6 }}>
                  <div
                    style={{
                      fontWeight:
                        ouvrage.id === activeOuvrageId
                          ? theme.fontWeights.semiBold
                          : theme.fontWeights.normal,
                      color:
                        ouvrage.id === activeOuvrageId
                          ? theme.colors.text.primary
                          : theme.colors.text.body,
                    }}
                  >
                    Ouvrage — {ouvrage.label}
                  </div>

                  <div style={{ paddingLeft: 10, marginTop: 2 }}>
                    {(ouvrage.levels ?? []).map((level) => (
                      <div
                        key={level.id}
                        style={{
                          color:
                            level.id === activeLevelId
                              ? theme.colors.text.primary
                              : theme.colors.text.body,
                        }}
                      >
                        Level — {level.label}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  /*
  ================================================================
  RENDER FINAL
  ================================================================
  */

  return (
    <div
      style={{
        width: 190,
        minWidth: 190,
        borderRight: `1px solid ${theme.colors.borders.panel}`,
        background: theme.colors.background.surface,
        padding: 8,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      {/* ----------------------------------------------------------
          TABS HAUT DE PANNEAU
      ---------------------------------------------------------- */}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 14,
        }}
      >
        <TabButton
          label="Layout"
          active={activeTab === "layout"}
          onClick={() => setActiveTab("layout")}
        />

        <TabButton
          label="Structure"
          active={activeTab === "structure"}
          onClick={() => setActiveTab("structure")}
        />
      </div>

      {/* ----------------------------------------------------------
          CONTENU ACTIF
      ---------------------------------------------------------- */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflowY: "auto",
          paddingRight: 2,
        }}
      >
        {activeTab === "layout" ? renderLayoutTab() : renderStructureTab()}
      </div>
    </div>
  );
};

export default DrawSiteLevelSidebar;
